/* eslint-disable react/jsx-handler-names */
import { useCallback, useRef } from 'react';
import { useDrag } from '@visx/drag';
import Annotation from "./Annotation.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const defaultDragHandleProps = {
  r: 10,
  fill: 'transparent',
  stroke: '#777',
  strokeDasharray: '4,2',
  strokeWidth: 2
};
export default function EditableAnnotation(_ref) {
  let {
    canEditLabel = true,
    canEditSubject = true,
    children,
    dx: labelDx = 0,
    dy: labelDy = 0,
    height,
    labelDragHandleProps,
    onDragEnd,
    onDragMove,
    onDragStart,
    subjectDragHandleProps,
    width,
    x: subjectX = 0,
    y: subjectY = 0
  } = _ref;
  // chicken before the egg, we need these to reference drag state
  // in drag callbacks which are defined before useDrag() state is available
  const subjectDragRef = useRef(undefined);
  const labelDragRef = useRef(undefined);
  const handleDragStart = useCallback(_ref2 => {
    let {
      event
    } = _ref2;
    if (onDragStart) {
      onDragStart({
        event,
        x: subjectX + (subjectDragRef.current?.dx ?? 0),
        y: subjectY + (subjectDragRef.current?.dy ?? 0),
        dx: labelDx + (labelDragRef.current?.dx ?? 0),
        dy: labelDy + (labelDragRef.current?.dy ?? 0)
      });
    }
  }, [labelDx, labelDy, onDragStart, subjectX, subjectY]);
  const handleDragMove = useCallback(_ref3 => {
    let {
      event
    } = _ref3;
    if (onDragMove) {
      onDragMove({
        event,
        x: subjectX + (subjectDragRef.current?.dx ?? 0),
        y: subjectY + (subjectDragRef.current?.dy ?? 0),
        dx: labelDx + (labelDragRef.current?.dx ?? 0),
        dy: labelDy + (labelDragRef.current?.dy ?? 0)
      });
    }
  }, [labelDx, labelDy, onDragMove, subjectX, subjectY]);
  const handleDragEnd = useCallback(_ref4 => {
    let {
      event
    } = _ref4;
    if (onDragEnd) {
      onDragEnd({
        event,
        x: subjectX + (subjectDragRef.current?.dx ?? 0),
        y: subjectY + (subjectDragRef.current?.dy ?? 0),
        dx: labelDx + (labelDragRef.current?.dx ?? 0),
        dy: labelDy + (labelDragRef.current?.dy ?? 0)
      });
    }
  }, [labelDx, labelDy, onDragEnd, subjectX, subjectY]);
  const subjectDrag = useDrag({
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    x: subjectX,
    y: subjectY
  });
  const labelDrag = useDrag({
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    x: labelDx,
    y: labelDy
  });

  // enable referencing these in the callbacks defined before useDrag is called
  subjectDragRef.current = subjectDrag;
  labelDragRef.current = labelDrag;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Annotation, {
      x: subjectX + subjectDrag.dx,
      y: subjectY + subjectDrag.dy,
      dx: labelDx + labelDrag.dx,
      dy: labelDy + labelDrag.dy,
      children: children
    }), subjectDrag.isDragging && /*#__PURE__*/_jsx("rect", {
      width: width,
      height: height,
      onMouseMove: subjectDrag.dragMove,
      onMouseUp: subjectDrag.dragEnd,
      fill: "transparent"
    }), canEditSubject && /*#__PURE__*/_jsx("circle", {
      cx: subjectX,
      cy: subjectY,
      transform: `translate(${subjectDrag.dx},${subjectDrag.dy})`,
      onMouseMove: subjectDrag.dragMove,
      onMouseUp: subjectDrag.dragEnd,
      onMouseDown: subjectDrag.dragStart,
      onTouchStart: subjectDrag.dragStart,
      onTouchMove: subjectDrag.dragMove,
      onTouchEnd: subjectDrag.dragEnd,
      cursor: subjectDrag.isDragging ? 'grabbing' : 'grab',
      ...defaultDragHandleProps,
      ...subjectDragHandleProps
    }), labelDrag.isDragging && /*#__PURE__*/_jsx("rect", {
      width: width,
      height: height,
      onMouseMove: labelDrag.dragMove,
      onMouseUp: labelDrag.dragEnd,
      fill: "transparent"
    }), canEditLabel && /*#__PURE__*/_jsx("circle", {
      cx: subjectX + subjectDrag.dx + labelDx,
      cy: subjectY + subjectDrag.dy + labelDy,
      transform: `translate(${labelDrag.dx},${labelDrag.dy})`,
      onMouseMove: labelDrag.dragMove,
      onMouseUp: labelDrag.dragEnd,
      onMouseDown: labelDrag.dragStart,
      onTouchStart: labelDrag.dragStart,
      onTouchMove: labelDrag.dragMove,
      onTouchEnd: labelDrag.dragEnd,
      cursor: labelDrag.isDragging ? 'grabbing' : 'grab',
      ...defaultDragHandleProps,
      ...labelDragHandleProps
    })]
  });
}